import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import styled from '@emotion/styled';
import { TopHeader } from 'ui/components/panes/Header/TopHeader';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { ForgotPassword } from 'ui/components/company/screens/modals/ForgotPassword/ForgotPassword';
import { useLoginForm } from './useLoginForm';
import { mqMin } from 'utils/responsive';

const Wrapper = styled.div`
    overflow-y: scroll;
    height: 100vh;
    @supports (height: 100svh) {
        height: 100svh;
    }
`;

const Title = styled.h1`
    font-size: var(--font-xx-large);
    font-weight: var(--font-bold);
    text-align: center;
    margin: 40px 30px 0 30px;

    ${mqMin('NARROW')} {
        margin: 60px 30px 0 30px;
    }
`;

const Form = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: var(-white);
    max-width: 500px;
    margin-inline: auto;
    padding: 40px 16px;

    .input {
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;
        margin-bottom: 10px;

        ${mqMin('NARROW')} {
            margin-bottom: 40px;
        }
    }
    .button {
        margin-block: 30px 16px;
    }
`;

const RegistrationButtonWrapper = styled.div`
    margin-block: 16px;
    margin-bottom: 30px;
`;

interface ActionButtonProps {
    bgColor: string;
    disabled?: boolean;
}

const ActionButton = styled.button<ActionButtonProps>`
    width: 100%;
    min-width: 300px;
    padding: 18px;
    outline: none;
    border-radius: 60px;
    background-color: ${(props) => props.bgColor};
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    color: var(--white);
    white-space: nowrap;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.disabled ? 0.2 : 1)};

    ${mqMin('NARROW')} {
        width: 400px;
        padding: 22px;
        font-size: var(--font-xx-large);
    }

    &:not(:disabled):hover {
        opacity: 0.7;
    }
`;

const RegistrationButton = styled.button`
    width: 100%;
    min-width: 300px;
    padding: 18px;
    outline: none;
    border-radius: 60px;
    background-color: #fff;
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    color: var(--main-color-blue);
    border: 2px solid var(--main-color-blue);
    white-space: nowrap;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.disabled ? 0.2 : 1)};

    ${mqMin('NARROW')} {
        width: 400px;
        padding: 22px;
        font-size: var(--font-xx-large);
    }

    &:not(:disabled):hover {
        opacity: 0.7;
    }
`;

/**
 * 企業系ログインフォーム
 */
export const LoginForm: React.FC = () => {
    const { control, errors, redirectIfLoggedIn, loading, login, register } = useLoginForm();

    useEffect(() => {
        redirectIfLoggedIn();
    }, []);

    return (
        <Wrapper>
            <TopHeader />
            <Title>売り手企業ログイン</Title>
            <Form>
                <div className="input">
                    <FormLabel title="メールアドレス" isRequired />
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <FormInput {...field} placeholder="email" hasError={!!errors.email?.message} />
                        )}
                    />
                </div>
                <div className="input">
                    <FormLabel title="パスワード" isRequired />
                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <FormInput
                                {...field}
                                type="password"
                                placeholder="password"
                                hasError={!!errors.password?.message}
                            />
                        )}
                    />
                </div>

                <span className="button">
                    <ActionButton
                        disabled={loading}
                        onClick={login}
                        bgColor="var(--main-color-blue)"
                        style={{ maxWidth: '400px' }}
                    >
                        ログイン
                    </ActionButton>
                </span>
                <div>または</div>
                <RegistrationButtonWrapper>
                    <RegistrationButton disabled={loading} onClick={register} style={{ maxWidth: '400px' }}>
                        新規登録
                    </RegistrationButton>
                </RegistrationButtonWrapper>

                <span className="forgot">
                    <ForgotPassword />
                </span>
            </Form>
        </Wrapper>
    );
};
